// Floating dark-mode toggle for local review. Writes to the preferences
// adapter and flips `data-theme` on <html>. Respects `prefers-color-scheme`
// until the user manually toggles.
import { RADIUS, T, TRANSITION } from '@/design/tokens';
import { getPreference, setPreference } from '@/lib/preferences';
import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | null;

function readStored(): Theme {
  const v = getPreference('gg-theme');
  return v === 'light' || v === 'dark' ? v : null;
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === null) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readStored());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const isDark = theme === 'dark' || (theme === null && systemPrefersDark());

  const flip = () => {
    const next: Theme = isDark ? 'light' : 'dark';
    setTheme(next);
    setPreference('gg-theme', next);
  };

  return (
    <button
      type="button"
      onClick={flip}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        color: T.text,
        borderRadius: RADIUS.pill,
        width: 44,
        height: 44,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: `background ${TRANSITION.fast}, border-color ${TRANSITION.fast}`,
      }}
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}
